import { fetchAiAnalyzeImage } from "@apis/videoCreatingService";
import CommonButton from "@components/CommonButton";
import { useSerializeCallback } from "@kalodata/tools";
import { useNeedAiAnalyzeImages } from "@pages/videoCreating/BasicInfo/useNeedAiAnalyzeImages";
import { ProductImage } from "@pages/videoCreating/videoCreatingSlice";
import { useMemo } from "react";
import { Trans } from "react-i18next";

export interface AIAnalyzeButtonProps {
  images: ProductImage[];
  productTitle: string;
  productCategory: string;
  onSuccess: (images: ProductImage[]) => void;
}

export const AIAnalyzeButton = (props: AIAnalyzeButtonProps) => {
  const { images, productTitle, productCategory, onSuccess } = props;
  const needAiAnalyzeImages = useNeedAiAnalyzeImages(images);
  const disabled = useMemo(() => {
    return !productTitle || !productCategory || images.length === 0;
  }, [productTitle, productCategory, images]);

  const handleAiAnalyze = useSerializeCallback(async () => {
    if (!productTitle || !productCategory || needAiAnalyzeImages.length === 0) return;
    const results = await fetchAiAnalyzeImage({
      title: productTitle,
      category: productCategory,
      imageUrls: needAiAnalyzeImages.map((image) => image.url),
    });
    const successImages = results.filter((result) => result.success);
    const nextImages = images
      .map<ProductImage | null>((e) => {
        const sendAiAnalyzeImage = needAiAnalyzeImages.find((image) => image.url === e.url);
        if (sendAiAnalyzeImage) {
          const aiAnalyzed = successImages.find((successImage) => successImage.imageUrl === e.url);
          const description = (aiAnalyzed?.description ?? e.description) || "";
          if (!aiAnalyzed) return null;
          return {
            ...e,
            description,
            aiAnalyzed: true,
          } satisfies ProductImage;
        }
        return e;
      })
      .filter((image): image is ProductImage => image !== null);
    onSuccess?.(nextImages);
  });

  const isAIAnalyzeLoading = handleAiAnalyze.isPending();

  return (
    <CommonButton
      type="primary"
      className="h-[40px] px-[24px] min-w-[140px]"
      disabled={disabled || isAIAnalyzeLoading}
      loading={isAIAnalyzeLoading}
      onClick={handleAiAnalyze}
    >
      <Trans>AI Analyze</Trans>
    </CommonButton>
  );
};
